import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { canCollapseAtom } from '@/components/layout/header/mobile-app-header/mobile-app-header.atoms';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { useRetrieveAppointmentOrSchedule } from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import { getSidebarPanelOverlayTriggerProps } from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import {
  trackScheduleSynced,
  trackScheduleViewed,
} from '@/models/analytics/features/trackSessionScheduleAnalytics';
import { Button } from '@heidi/design-system/button';
import { Calendar } from '@heidi/design-system/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { isSameDay } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Calendar as LucideCalendar, PanelLeftClose, RefreshCw, X } from 'lucide-react';
import { memo, useCallback } from 'react';

function SessionBarScheduleTabHeaderButtons() {
  const isMobile = useIsMobile();
  const openedSessionTab = useAtomValue(openedSessionTabAtom);

  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const { refetch: refetchApptOrSchedule, isPending: isApptOrSchedulePending } =
    useRetrieveAppointmentOrSchedule();

  const [scheduleDate, setScheduleDate] = useAtom(scheduleDateAtom);

  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isActionDisabled = isApptOrSchedulePending || !integrationEhrProvider;

  const handleSync = useCallback(() => {
    void refetchApptOrSchedule();
    trackScheduleSynced({
      ehr_provider: integrationEhrProvider,
    });
  }, [integrationEhrProvider, refetchApptOrSchedule]);
  const isToday = isSameDay(scheduleDate ?? new Date(), new Date());

  const canCollapse = useAtomValue(canCollapseAtom);
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  if (openedSessionTab !== openedSessionTabEnum.UPCOMING) {
    return <></>;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          <Button
            disableAutomaticSvgSizing
            variant={isSidebarPanelEnabled ? 'panel_ghost' : 'ghost'}
            size="xxs"
            iconOnly
            className={cn(
              'p-1.5',
              !isSidebarPanelEnabled && 'text-text-secondary',
              isMobile && 'p-1'
            )}
            onClick={handleSync}
            disabled={isActionDisabled}
            data-testid="sidebar-button-sync-upcoming-sessions"
          >
            <RefreshCw
              size={isMobile ? 24 : 12}
              className={cn(isApptOrSchedulePending && 'animate-spin')}
            />
          </Button>

          <Popover>
            <PopoverTrigger
              {...getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled)}
              asChild
            >
              <Button
                disableAutomaticSvgSizing
                size="xxs"
                variant={
                  isSidebarPanelEnabled
                    ? 'panel_ghost'
                    : !isToday
                      ? 'default'
                      : isMobile
                        ? 'ghost'
                        : 'secondary'
                }
                data-active={isSidebarPanelEnabled ? !isToday : undefined}
                className={cn(
                  'p-1.5',
                  !isSidebarPanelEnabled &&
                    cn('text-text-secondary', !isToday && 'text-text-primary-invert'),
                  isMobile && 'p-1'
                )}
                disabled={isActionDisabled}
              >
                <LucideCalendar size={isMobile ? 24 : 12} />
                {!isToday && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setScheduleDate(undefined);
                    }}
                  >
                    <X size={12} />
                  </div>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit">
              <Calendar
                mode="single"
                selected={scheduleDate}
                onSelect={(date) => {
                  setScheduleDate(date);
                  trackScheduleViewed({
                    date,
                    ehr_provider: integrationEhrProvider,
                  });
                }}
              />
            </PopoverContent>
          </Popover>

          {isMobile && canCollapse !== false && (
            <div className="flex items-center gap-2">
              <Button
                size="lg"
                disableAutomaticSvgSizing
                variant="ghost"
                className="size-fit min-h-0 min-w-0 p-1.5"
                disabled={isApptOrSchedulePending}
                onClick={() => {
                  if (isMobile) {
                    setIsMobileHeaderSheetOpen(false);
                  }
                }}
              >
                <PanelLeftClose
                  size={24}
                  className={cn(
                    'flex cursor-pointer items-center border-0 bg-surface text-3xl text-text-secondary'
                  )}
                />
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default memo(SessionBarScheduleTabHeaderButtons);
